import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import atatus from 'atatus';
import useGTM from 'hooks/useGTM';
import { tagForm } from 'tagManager';

import FocusableHeader from 'components/focusableheader/FocusableHeader';
import AccountExistsModal from 'components/modals/AccountExistsModal';
import { AppRoutes } from 'routes';

import registrationErrors from '../../components/errors/registrationErrors';
import AccountValidationContent from '../../components/registration/AccountValidationContent';
import SignUpContent from '../../components/registration/SignUpContent';
import ValidateUserContent from '../../components/registration/ValidateUserContent';
import OverlaySpinner from '../../components/spinner/OverlaySpinner';
import StepHeader from '../../components/sto/StepHeader';
import useLocalization from '../../hooks/useLocalization';
import { validateAccount } from '../../services/AccountService';
import scrollTo from '../../utils/scrollTo';

const registrationSteps = {
    accountValidation: 'accountValidation',
    userCreation: 'userCreation',
    userVerification: 'userVerification',
};

function Registration() {
    const { localizedPush } = useLocalization();
    const { stepChange } = useGTM();
    const [currentStep, updateStep] = useState('accountValidation');
    const [verificationkey, updateVerificationkey] = useState(null);
    const [username, updateUserName] = useState(null);
    const [email, setEmail] = useState(null);
    const [error, setError] = useState(null);
    const [displayModal, updateModalVisibility] = useState(false);
    const [isValidatingAccount, setIsValidatingAccount] = useState(false);
    const [stepValue, updateSteps] = useState([
        {
            status: 'process',
            title: <FormattedMessage id="pages.registration.steps.account-verification" />,
        },
        {
            status: 'wait',
            title: <FormattedMessage id="pages.registration.steps.account-creation" />,
        },
        {
            status: 'wait',
            title: <FormattedMessage id="pages.registration.steps.account-validation" />,
        },
    ]);

    useEffect(() => {
        let stepChangeData;
        switch (currentStep) {
            case registrationSteps['accountValidation']:
                stepChangeData = { currentPageName: 'registrationStep1' };
                break;
            case registrationSteps['userCreation']:
                stepChangeData = {
                    currentPageName: 'registrationStep2',
                    previousPageName: 'registrationStep1',
                };
                break;
            case registrationSteps['userVerification']:
                stepChangeData = {
                    currentPageName: 'registrationStep3',
                    previousPageName: 'registrationStep2',
                };
                break;
            default:
                break;
        }
        stepChange(stepChangeData);
    }, [currentStep, stepChange]);

    const onModalClosing = () => {
        updateModalVisibility(false);
        localizedPush(AppRoutes.loginPage, {
            queryParams: { fromRegistration: true },
        });
    };

    const onAccountValidationSubmit = async (accountInfo) => {
        scrollTo(0, 0);
        setError(null);
        setIsValidatingAccount(true);
        atatus.startTransaction('Registration-Step-1-Account-Validation');
        try {
            const { email: _email, verificationKey: vkey } = await validateAccount(accountInfo);
            atatus.endTransaction('Registration-Step-1-Account-Validation');
            tagForm({
                fields: {
                    account_number: accountInfo.accountNumber,
                    phone_number: accountInfo.phoneNumber,
                    postal_code: accountInfo.postalCode,
                },
                name: 'registration-step-1',
                success: true,
            });
            updateVerificationkey(vkey);
            setEmail(_email);
            updateStepHeader(1);
            updateStep('userCreation');
        } catch (e) {
            if (e.response && e.response.status === 454) {
                updateModalVisibility(true);
                return;
            }
            const errMessage = registrationErrors[e.response && e.response.status];
            atatus.failTransaction('Registration-Step-1-Account-Validation');
            if (errMessage) {
                setError(errMessage);
            } else {
                tagForm({
                    fields: {
                        account_number: accountInfo.accountNumber,
                        phone_number: accountInfo.phoneNumber,
                        postal_code: accountInfo.postalCode,
                    },
                    message: e.message,
                    name: 'registration-step-1',
                    success: false,
                });
                localizedPush(AppRoutes.connectionFailurePage);
            }
        } finally {
            setIsValidatingAccount(false);
        }
    };

    const onSignUpSubmit = (username, maskedEmail) => {
        scrollTo(0, 0);
        updateUserName(username);
        setEmail(maskedEmail);
        updateStepHeader(2);
        updateStep('userVerification');
    };

    const updateStepHeader = (stepIndex) => {
        stepValue[stepIndex].status = 'process';
        stepValue[stepIndex - 1].status = 'finished';
        updateSteps(stepValue);
    };

    let stepContent = <div />;
    switch (currentStep) {
        case registrationSteps['accountValidation']:
            stepContent = <AccountValidationContent error={error} onSubmit={onAccountValidationSubmit} />;
            break;
        case registrationSteps['userCreation']:
            stepContent = <SignUpContent email={email} onSubmit={onSignUpSubmit} verificationkey={verificationkey} />;
            break;
        case registrationSteps['userVerification']:
            stepContent = <ValidateUserContent email={email} username={username} />;
            break;
        default:
            break;
    }

    return (
        <React.Fragment>
            <div className="registration-page-content pprlpay-page-wrapper grey-banner">
                {isValidatingAccount && <OverlaySpinner />}

                <FocusableHeader>
                    <FormattedMessage id="pages.registration.title" />
                </FocusableHeader>

                <hr aria-hidden className="fs-bar orange" />
                <div>
                    <StepHeader steps={stepValue} />
                </div>
                {stepContent}
            </div>
            <div>
                <AccountExistsModal onOk={onModalClosing} visible={displayModal} />
            </div>
        </React.Fragment>
    );
}

export default Registration;
